import { useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowUpRight,
  Calculator,
  Check,
  CheckCircle2,
  FileDown,
  Gauge,
  HandCoins,
  Handshake,
  Lock,
  Minus,
  PiggyBank,
  Plus,
  Receipt,
  Users,
  Warehouse,
  X,
} from "lucide-react";
import EventPageTemplate from "../components/EventPageTemplate";
import { IMG, CONTACTS } from "../data/site";
import useSEO from "../hooks/useSEO";
import { Reveal } from "../components/anim";
import axios from "axios";
import { toast } from "sonner";

const RATA_DRINK = 1.5;
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (n) =>
  new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR", useGrouping: "always" }).format(n);

const SPAZI = ["Fino a 20 m²", "Da 20 a 50 m²", "Oltre 50 m²", "Ampio spazio esterno", "Da definire insieme"];

const VANTAGGI = [
  {
    icon: PiggyBank,
    title: "Nessun investimento iniziale nel bar",
    text: "Niente banchi, erogatori o attrezzature da comprare: la struttura concordata la mettiamo noi.",
  },
  {
    icon: HandCoins,
    title: "Una partnership che genera ricavi",
    text: "L'organizzatore riceve un compenso sulle vendite del beverage: il tuo pubblico diventa un'opportunità commerciale.",
  },
  {
    icon: Receipt,
    title: "Vendita automatizzata e incassi tracciabili",
    text: "Transazioni registrate e rendicontate: procedure chiare per verificare gli incassi e la quota spettante.",
  },
  {
    icon: Gauge,
    title: "Capacità di servizio per grandi affluenze",
    text: "Spina e self service riducono le operazioni manuali e reggono i picchi di domanda, con l'obiettivo di limitare le code.",
  },
  {
    icon: Users,
    title: "Personale e organizzazione in un'unica soluzione",
    text: "Coordiniamo le risorse operative previste dall'accordo: niente ricerca di personale in autonomia.",
  },
  {
    icon: Warehouse,
    title: "Nessun acquisto diretto di prodotti, nessuna giacenza in carico all'organizzatore",
    text: "Approvvigionamenti, fusti aperti e prodotti invenduti: gestiti da noi secondo gli accordi, senza capitale anticipato.",
  },
  {
    icon: Handshake,
    title: "Un unico partner, dall'allestimento al servizio",
    text: "Attrezzature, bevande, ghiaccio, bicchieri, assistenza e gestione operativa: un solo interlocutore.",
  },
];

const CONFRONTO_BAR = [
  "Acquisto delle attrezzature.",
  "Anticipo del capitale per le bevande.",
  "Gestione dei fusti aperti e delle giacenze.",
  "Ricerca e coordinamento del personale.",
  "Gestione dei rifornimenti.",
  "Organizzazione dei pagamenti e rendicontazione.",
  "Rischio di prodotti invenduti.",
  "Responsabilità operative a carico dell'organizzatore.",
];

const CONFRONTO_SPRIZZIAMOCI = [
  "Struttura e attrezzature previste dal contratto.",
  "Prodotti gestiti da Sprizziamoci.",
  "Rischio delle giacenze assunto da Sprizziamoci nei termini concordati.",
  "Personale messo a disposizione secondo l'accordo.",
  "Gestione organizzata dei rifornimenti.",
  "Transazioni registrate e rendicontazione concordata.",
  "Nessun acquisto diretto di prodotti da parte dell'organizzatore.",
  "Un partner responsabile della gestione operativa concordata.",
];

const TRASPARENZA = [
  {
    t: "Definizione delle condizioni economiche",
    d: "Definiamo insieme le condizioni economiche della partnership prima dell'evento, in un accordo chiaro.",
  },
  {
    t: "Installazione e avvio del servizio",
    d: "Allestiamo la postazione, prepariamo i sistemi di erogazione e avviamo il servizio negli orari concordati.",
  },
  {
    t: "Registrazione delle vendite",
    d: "Ogni vendita viene registrata attraverso il sistema di vendita adottato per la manifestazione.",
  },
  {
    t: "Rendicontazione degli incassi",
    d: "Gli incassi vengono rendicontati secondo le modalità e i tempi stabiliti nell'accordo.",
  },
  {
    t: "Quota spettante all'organizzatore",
    d: "Calcoliamo e riconosciamo la quota spettante all'organizzatore secondo il contratto.",
  },
];

function CalcolatoreConfronto() {
  const [c, setC] = useState({
    fusti: 4,
    costo: 100,
    pct: 50,
    addetti: 2,
    ore: 6,
    orario: 15,
    responsabile: 80,
    burocrazia: 150,
  });
  const setV = (k, d, min, max) => setC((v) => ({ ...v, [k]: Math.max(min, Math.min(max, v[k] + d)) }));
  const setNum = (k, e, min, max) =>
    setC((v) => ({ ...v, [k]: Math.max(min, Math.min(max, Math.round(Number(e.target.value) || min))) }));

  const capitale = c.fusti * c.costo;
  const giacenze = Math.round(((capitale * c.pct) / 100) * 100) / 100;
  const montaggio = 3 * 3 * c.orario;
  const servizio = c.addetti * c.ore * c.orario;
  const smontaggio = 3 * 2 * c.orario;
  const totale = capitale + giacenze + montaggio + servizio + c.responsabile + smontaggio + c.burocrazia;
  const squadra = Math.ceil(3000 / 75);

  const righe = [
    { l: "Capitale bevande: fusti acquistati", sub: `${c.fusti} fusti × ${c.costo} € (costo medio)`, v: capitale },
    { l: "Giacenze invendute", sub: `${c.pct}% del capitale, rischio in carico all'organizzatore`, v: giacenze },
    { l: "Montaggio attrezzatura", sub: "3 addetti × 3 ore", v: montaggio },
    { l: "Servizio durante l'evento", sub: `${c.addetti} addetti × ${c.ore} ore`, v: servizio },
    { l: "Responsabile abilitato alla somministrazione", sub: "quota indicativa per l'evento", v: c.responsabile },
    { l: "Smontaggio e riassetto", sub: "3 addetti × 2 ore", v: smontaggio },
    { l: "Burocrazia e incassi", sub: "adempimenti, gestionale, gestione fiscale del venduto", v: c.burocrazia },
  ];

  const stepper = (id, label, k, d, min, max) => (
    <div>
      <label htmlFor={`calc-${id}`} className="block text-[11px] uppercase tracking-[0.18em] text-white/50 mb-2">
        {label}
      </label>
      <div className="flex items-center gap-2">
        <button
          type="button"
          data-testid={`pub-calc-${id}-minus`}
          onClick={() => setV(k, -d, min, max)}
          className="h-10 w-10 shrink-0 rounded-full border border-white/20 text-white/70 hover:border-brand-orange hover:text-brand-orange transition-colors duration-200"
          aria-label={`Riduci ${label}`}
        >
          <Minus size={14} className="mx-auto" />
        </button>
        <input
          id={`calc-${id}`}
          type="number"
          min={min}
          max={max}
          data-testid={`pub-calc-${id}`}
          value={c[k]}
          onChange={(e) => setNum(k, e, min, max)}
          className="field !py-2.5 text-center font-display text-xl font-semibold"
        />
        <button
          type="button"
          data-testid={`pub-calc-${id}-plus`}
          onClick={() => setV(k, d, min, max)}
          className="h-10 w-10 shrink-0 rounded-full border border-white/20 text-white/70 hover:border-brand-orange hover:text-brand-orange transition-colors duration-200"
          aria-label={`Aumenta ${label}`}
        >
          <Plus size={14} className="mx-auto" />
        </button>
      </div>
    </div>
  );

  return (
    <div data-testid="pub-simulatore-giacenze" className="rounded-3xl border border-white/[0.08] bg-brand-surface/30 p-8 md:p-10">
      <p className="eyebrow mb-3">Simulatore: il costo del bar gestito in proprio</p>
      <h3 className="font-display text-2xl md:text-4xl font-semibold leading-tight">
        Quanto costa (e rischia) un'organizzazione che gestisce il bar da sola?
      </h3>
      <p className="mt-4 text-sm leading-relaxed text-white/60 max-w-3xl">
        Aggiorna i valori con i numeri della tua manifestazione: la tabella calcola, voce per
        voce, i costi e i rischi del bar gestito in proprio. Le referenze alla spina sono decine:
        dalla birra ai cocktail pre-mix, prosecco, vini e analcolici.
      </p>

      <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
        {stepper("fusti", "Fusti acquistati", "fusti", 1, 1, 500)}
        {stepper("costo", "Costo medio fusto (€)", "costo", 5, 5, 500)}
        {stepper("pct", "Prodotto invenduto (%)", "pct", 5, 0, 100)}
        {stepper("addetti", "Addetti al servizio", "addetti", 1, 0, 100)}
        {stepper("ore", "Ore di evento", "ore", 1, 1, 24)}
        {stepper("orario", "Costo orario €/h", "orario", 1, 5, 100)}
        {stepper("responsabile", "Responsabile abilitato (€)", "responsabile", 10, 0, 1000)}
        {stepper("burocrazia", "Burocrazia e fiscalità (€)", "burocrazia", 10, 0, 2000)}
      </div>

      <div className="mt-10 overflow-hidden rounded-2xl border border-white/[0.08]">
        <div className="grid grid-cols-[1fr_auto_auto] md:grid-cols-[1.8fr_0.9fr_0.9fr] gap-x-3 md:gap-x-6 bg-brand-ink/80 px-4 md:px-6 py-3">
          <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-white/40">Voce</span>
          <span className="text-right text-[10px] font-semibold uppercase tracking-[0.18em] text-white/40">Bar in proprio</span>
          <span className="text-right text-[10px] font-semibold uppercase tracking-[0.18em] text-brand-orange">Sprizziamoci</span>
        </div>
        {righe.map((r) => (
          <div key={r.l} data-testid="pub-calc-riga" className="grid grid-cols-[1fr_auto_auto] md:grid-cols-[1.8fr_0.9fr_0.9fr] gap-x-3 md:gap-x-6 items-center border-t border-white/[0.06] px-4 md:px-6 py-3.5">
            <div>
              <p className="text-sm text-white/80 leading-snug">{r.l}</p>
              <p className="mt-0.5 text-[11px] text-white/40">{r.sub}</p>
            </div>
            <span data-testid="pub-calc-costi" className="text-right text-sm font-semibold text-white whitespace-nowrap">{fmt(r.v)}</span>
            <span className="text-right text-xs text-white/45 whitespace-nowrap">Incluso</span>
          </div>
        ))}
        <div className="grid grid-cols-[1fr_auto_auto] md:grid-cols-[1.8fr_0.9fr_0.9fr] gap-x-3 md:gap-x-6 items-center border-t-2 border-brand-orange/40 bg-brand-orange/[0.07] px-4 md:px-6 py-4">
          <p className="text-sm font-semibold uppercase tracking-[0.08em] text-white">Costo totale a carico dell'organizzatore</p>
          <span data-testid="pub-calc-totale-bar" className="text-right font-display text-xl md:text-2xl font-semibold text-brand-orange whitespace-nowrap">{fmt(totale)}</span>
          <span data-testid="pub-calc-totale-sprizziamoci" className="text-right font-display text-xl md:text-2xl font-semibold text-brand-orange whitespace-nowrap">{fmt(0)}</span>
        </div>
      </div>

      <div className="mt-8 grid gap-5 md:grid-cols-2">
        <div className="rounded-2xl border border-white/[0.08] bg-brand-ink/60 p-6 text-center">
          <p className="eyebrow">Bar gestito in proprio</p>
          <p data-testid="pub-calc-differenza" className="mt-3 font-display text-4xl md:text-5xl font-semibold">{fmt(totale)}</p>
          <p className="mt-3 text-sm leading-relaxed text-white/55">
            di costi e rischi prima di vendere un solo drink: capitale, personale, burocrazia e
            prodotto che resta invenduto.
          </p>
        </div>
        <div className="rounded-2xl border border-brand-orange/40 bg-brand-orange/[0.07] p-6 text-center">
          <p className="eyebrow">Con la partnership Sprizziamoci</p>
          <p className="mt-3 font-display text-4xl md:text-5xl font-semibold text-brand-orange">{fmt(0)}</p>
          <p className="mt-3 text-sm leading-relaxed text-white/75">
            di costi iniziali per l'organizzatore: struttura, prodotto, personale, ghiaccio e
            bicchieri secondo l'accordo. E su ogni drink venduto il partner socio incassa 1,50 €.
          </p>
        </div>
      </div>

      <div className="mt-8 rounded-2xl border border-white/[0.08] p-6">
        <p className="eyebrow mb-3">L'erogazione: la differenza fisica</p>
        <p className="text-sm leading-relaxed text-white/70">
          Nel bar tradizionale <strong className="text-white">1 addetto serve 70-80 cocktail all'ora</strong>:
          nei picchi si formano le code. Il sistema Sprizziamoci eroga, come capacità teorica
          dichiarata, <strong className="text-brand-orange">fino a 3.000 drink all'ora</strong>:
          per avvicinare quella capacità servirebbe una squadra di circa {squadra} addetti (stima
          3.000 ÷ 75).
        </p>
      </div>

      <p className="mt-6 text-[11px] leading-relaxed text-white/40">
        Simulazione indicativa con valori modificabili: il valore effettivo delle giacenze dipende
        dai consumi, dalle condizioni di conservazione e dalla possibilità di riutilizzare le
        scorte. La capacità di erogazione è una dichiarazione teorica da verificare con il
        fornitore e mediante test operativi: non costituisce una prestazione garantita o
        certificata, e la produttività reale dipende dal numero di erogatori, dai tempi di
        pagamento, dai rifornimenti e dal flusso del pubblico. Con Sprizziamoci il rischio
        economico delle giacenze viene gestito dal partner secondo il contratto, senza che
        l'organizzatore debba acquistare direttamente le bevande.
      </p>
    </div>
  );
}

export default function EventiPubblici() {
  useSEO(
    "Servizio bar e beverage per festival e grandi eventi | Sprizziamoci",
    "Bar self-service per manifestazioni: gestione beverage per eventi pubblici, festival e sagre. Partnership beverage per organizzatori, senza investimenti nel bar."
  );
  const [drinks, setDrinks] = useState(500);
  const utile = drinks * RATA_DRINK;
  const setDrinksSafe = (v) => setDrinks(Math.max(0, Math.min(50000, Math.round(v) || 0)));
  const [iscritto, setIscritto] = useState(() => localStorage.getItem("sprz-partner-iscritto") === "1");
  const [form, setForm] = useState({ nome_cognome: "", azienda: "", email: "", telefono: "", spazio_disponibile: "" });
  const [err, setErr] = useState({});
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => {
    setForm((f) => ({ ...f, [k]: e.target.value }));
    if (err[k]) setErr((v) => ({ ...v, [k]: undefined }));
  };

  const submitIscrizione = async (e) => {
    e.preventDefault();
    const errs = {};
    if (form.nome_cognome.trim().length < 2) errs.nome_cognome = "Inserisci nome e cognome";
    if (form.azienda.trim().length < 2) errs.azienda = "Inserisci l'organizzazione";
    if (!/^\S+@\S+\.\S+$/.test(form.email)) errs.email = "Inserisci una email valida";
    if (form.telefono.trim().length < 6) errs.telefono = "Inserisci un telefono valido";
    if (form.spazio_disponibile.length < 2) errs.spazio_disponibile = "Seleziona lo spazio disponibile";
    setErr(errs);
    if (Object.keys(errs).length) return;
    setSending(true);
    try {
      await axios.post(`${API}/partner-requests`, {
        ...form,
        ruolo: "Organizzatore manifestazioni",
        tipologia_attivita: "Manifestazioni e festival",
        messaggio: "Iscrizione dalla pagina Eventi Pubblici: voglio valutare la partnership socio e il potenziale guadagno della mia manifestazione.",
      });
      localStorage.setItem("sprz-partner-iscritto", "1");
      setIscritto(true);
      toast.success("Iscrizione ricevuta! Calcolo del guadagno sbloccato.");
      setTimeout(
        () => document.querySelector("#calcolo-utile")?.scrollIntoView({ behavior: "smooth", block: "center" }),
        400
      );
    } catch {
      toast.error("Invio non riuscito: riprova tra poco.");
    }
    setSending(false);
  };

  const campo = (id, label, key, tipo = "text", placeholder = "") => (
    <div>
      <label htmlFor={`pub-${id}`} className="block text-[11px] uppercase tracking-[0.18em] text-white/50 mb-2">
        {label}
      </label>
      <input
        id={`pub-${id}`}
        type={tipo}
        data-testid={`pub-field-${id}`}
        className={`field ${err[key] ? "border-red-500/70" : ""}`}
        placeholder={placeholder}
        value={form[key]}
        onChange={set(key)}
      />
      {err[key] && <p className="mt-2 text-xs text-red-400">{err[key]}</p>}
    </div>
  );

  return (
    <article data-testid="eventi-pubblici-page">
      <EventPageTemplate
        eyebrow="Eventi pubblici e manifestazioni"
        titleLines={["Dove c'è gente,", "c'è voglia di brindare."]}
        intro="Manifestazioni, festival, feste di piazza ed eventi musicali: ospita Sprizziamoci e trasforma il tuo pubblico in guadagno per l'organizzazione."
        image={IMG.bancoEvento}
        imageAlt="Il banco Sprizziamoci con tenda arancione ed erogatori alla spina all'ingresso di una manifestazione serale"
        occasions={[
          "Manifestazioni e feste di piazza",
          "Festival ed eventi musicali",
          "Iniziative di enti e associazioni",
          "Eventi organizzati da operatori del settore",
        ]}
        paragraphs={[
          "Banco riconoscibile e sistema organizzato pensato per i grandi numeri: postazioni multiple dedicate al flusso di pubblico, personale incluso. Ospitare Sprizziamoci non costa nulla alla tua organizzazione.",
        ]}
        cta="Ospita Sprizziamoci"
        ctaTo="#iscrizione"
        selfNote={false}
      />

      {/* --- Partner per organizzatori --- */}
      <section id="partner-organizzatori" data-testid="pub-partner-organizzatori" className="pb-24 md:pb-32 scroll-mt-24">
        <div className="mx-auto max-w-7xl px-5 md:px-8">
          <Reveal>
            <p className="eyebrow mb-4">Partner per organizzatori · Eventi pubblici</p>
            <h2 className="font-display text-4xl md:text-6xl font-semibold leading-[1.05] max-w-5xl">
              IL TUO EVENTO, IL NOSTRO BAR. I TUOI INCASSI.
            </h2>
            <p className="mt-8 text-base md:text-lg leading-relaxed text-white/85 max-w-3xl font-medium">
              Trasforma il beverage in una fonte di ricavo per la tua manifestazione, senza dover
              acquistare prodotti, attrezzature e organizzare autonomamente il servizio.
            </p>
            <p className="mt-5 text-sm md:text-lg leading-relaxed text-white/60 max-w-3xl">
              Organizzare un evento è già una grande impresa. Perché aggiungere anche le complessità
              di un bar? Con Sprizziamoci hai un partner che porta la struttura, i prodotti, il
              personale e un sistema di erogazione progettato per gestire anche grandi flussi di
              pubblico. Tu ti concentri sull'evento; noi ci occupiamo del beverage secondo gli
              accordi stabiliti.
            </p>
          </Reveal>
          <Reveal delay={0.08}>
            <a
              href="#calcolo-confronto"
              data-testid="pub-cta-calcolatore"
              className="mt-8 flex max-w-3xl flex-wrap items-center justify-between gap-4 rounded-2xl border-2 border-brand-orange bg-brand-orange/[0.08] px-6 py-5 transition-colors duration-300 hover:bg-brand-orange/[0.14]"
            >
              <span className="text-sm font-semibold text-white md:text-base">
                Conviene davvero? Verificalo subito: il calcolatore mette a confronto il costo del
                bar gestito in proprio con la partnership.
              </span>
              <span className="inline-flex items-center gap-2 rounded-full bg-brand-orange px-6 py-3 text-[11px] font-semibold uppercase tracking-[0.2em] text-brand-ink">
                Calcola ora <Calculator size={14} />
              </span>
            </a>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link
                to="/partner"
                data-testid="pub-cta-partner"
                className="inline-flex items-center gap-3 rounded-full bg-brand-orange px-8 py-4 text-xs font-semibold uppercase tracking-[0.2em] text-brand-ink hover:bg-[#ff9433] transition-colors duration-300"
              >
                Diventa partner Sprizziamoci <ArrowUpRight size={15} />
              </Link>
              <a
                href="#iscrizione"
                data-testid="pub-cta-proposta"
                className="inline-flex items-center gap-3 rounded-full border border-brand-orange/50 px-8 py-4 text-xs font-semibold uppercase tracking-[0.2em] text-brand-orange hover:bg-brand-orange hover:text-brand-ink transition-colors duration-300"
              >
                Richiedi una proposta per il tuo evento
              </a>
              <a
                href="/brochure-organizzatori.pdf"
                download="Brochure-organizzatori-Sprizziamoci.pdf"
                data-testid="pub-download-brochure"
                className="inline-flex items-center gap-2.5 rounded-full border border-white/25 px-8 py-4 text-xs font-semibold uppercase tracking-[0.2em] text-white hover:border-brand-orange hover:text-brand-orange transition-colors duration-300"
              >
                <FileDown size={15} /> Brochure per organizzatori (PDF)
              </a>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="mt-10 inline-flex flex-wrap items-center gap-4 rounded-2xl border border-brand-orange/40 bg-brand-orange/[0.07] px-6 py-4">
              <span className="font-display text-3xl md:text-4xl font-semibold text-brand-orange">1,50 €</span>
              <span className="text-sm text-white/75 max-w-md">
                a drink venduto per il partner socio. La remunerazione effettiva, le modalità di
                rendicontazione, i tempi di pagamento e le condizioni di partecipazione vengono
                formalizzati nell'accordo commerciale.
              </span>
            </div>
          </Reveal>
        </div>
      </section>

      {/* --- 7 vantaggi competitivi --- */}
      <section data-testid="pub-vantaggi" className="pb-24 md:pb-32">
        <div className="mx-auto max-w-7xl px-5 md:px-8">
          <Reveal>
            <p className="eyebrow mb-4">I vantaggi per la tua organizzazione</p>
            <h2 className="font-display text-3xl md:text-5xl font-semibold leading-tight max-w-4xl">
              Sette motivi per lasciare il bar a chi lo sa gestire.
            </h2>
          </Reveal>
          <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {VANTAGGI.map((v, i) => (
              <Reveal key={v.title} delay={i * 0.05} className={i === 0 ? "lg:col-span-2" : ""}>
                <div
                  data-testid={`pub-vantaggio-${i + 1}`}
                  className={`flex items-start gap-5 rounded-3xl border border-white/[0.07] bg-brand-surface/30 p-7 h-full transition-colors duration-300 hover:border-brand-orange/40 ${
                    i === 0 ? "lg:bg-brand-orange/[0.06] lg:border-brand-orange/30" : ""
                  }`}
                >
                  <div className="shrink-0">
                    <span className="font-display text-2xl font-semibold text-white/25">0{i + 1}</span>
                    <v.icon size={22} className="mt-2 text-brand-orange" />
                  </div>
                  <div>
                    <p className="font-display text-xl md:text-2xl font-semibold leading-snug">{v.title}</p>
                    <p className="mt-3 text-sm leading-relaxed text-white/55">{v.text}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* --- Confronto con il bar tradizionale --- */}
      <section data-testid="pub-confronto" className="pb-24 md:pb-32">
        <div className="mx-auto max-w-7xl px-5 md:px-8">
          <Reveal>
            <p className="eyebrow mb-4">Il confronto</p>
            <h2 className="font-display text-3xl md:text-5xl font-semibold leading-tight max-w-5xl">
              Perché gestire un bar in proprio quando puoi affidarti a Sprizziamoci?
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="mt-12 grid gap-6 md:grid-cols-2">
              <div className="rounded-3xl border border-white/[0.07] bg-white/[0.02] p-7 md:p-9">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-white/45 mb-6">
                  Bar gestito direttamente
                </p>
                <ul className="space-y-4">
                  {CONFRONTO_BAR.map((x) => (
                    <li key={x} className="flex items-start gap-3 text-sm leading-relaxed text-white/50">
                      <X size={16} className="shrink-0 mt-0.5 text-white/30" />
                      {x}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="rounded-3xl border border-brand-orange/40 bg-brand-orange/[0.06] p-7 md:p-9">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-brand-orange mb-6">
                  Partnership Sprizziamoci
                </p>
                <ul className="space-y-4">
                  {CONFRONTO_SPRIZZIAMOCI.map((x) => (
                    <li key={x} className="flex items-start gap-3 text-sm leading-relaxed text-white/85">
                      <Check size={16} strokeWidth={2.6} className="shrink-0 mt-0.5 text-brand-orange" />
                      {x}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* --- Calcolatore confronto costi bar in proprio --- */}
      <section id="calcolo-confronto" className="scroll-mt-24 pb-24 md:pb-32">
        <div className="mx-auto max-w-7xl px-5 md:px-8">
          <Reveal>
            <CalcolatoreConfronto />
          </Reveal>
        </div>
      </section>

      {/* --- Grandi numeri --- */}
      <section data-testid="pub-grandi-numeri" className="pb-24 md:pb-32">
        <div className="mx-auto max-w-7xl px-5 md:px-8">
          <Reveal>
            <p className="eyebrow mb-4">Servizio beverage per festival, sagre e concerti</p>
            <h2 className="font-display text-3xl md:text-5xl font-semibold leading-tight max-w-4xl">
              PROGETTATO PER I GRANDI NUMERI.
            </h2>
            <p className="mt-6 text-sm md:text-lg leading-relaxed text-white/65 max-w-3xl">
              Quando migliaia di persone si ritrovano nello stesso luogo, il servizio beverage deve
              essere all'altezza dell'evento. Sprizziamoci utilizza sistemi di erogazione alla spina
              e un modello operativo automatizzato progettato per aumentare la capacità di servizio
              e ridurre le operazioni manuali.
            </p>
          </Reveal>
          <div className="mt-12 grid gap-5 md:grid-cols-2">
            <Reveal>
              <div className="rounded-3xl border border-white/[0.07] bg-white/[0.02] p-8 h-full">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-white/45">Bar tradizionale</p>
                <p className="mt-4 text-sm leading-relaxed text-white/55">
                  Ogni consumazione passa da un operatore: shakerare, versare, incassare. Nei picchi
                  di affluenza le operazioni manuali si sommano e si formano le code.
                </p>
              </div>
            </Reveal>
            <Reveal delay={0.08}>
              <div className="rounded-3xl border border-brand-orange/40 bg-brand-orange/[0.06] p-8 h-full">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-brand-orange">
                  Il sistema Sprizziamoci
                </p>
                <p className="mt-4 text-sm leading-relaxed text-white/85">
                  Erogazione alla spina self service: l'ospite si versa il drink da solo e il
                  personale si concentra su rifornimenti, assistenza e qualità del servizio.
                </p>
              </div>
            </Reveal>
          </div>
          <Reveal delay={0.1}>
            <div className="mt-8 grid gap-5 md:grid-cols-2">
              <div className="rounded-3xl border border-white/[0.08] bg-brand-ink/60 p-8 text-center">
                <p className="font-display text-5xl md:text-6xl font-semibold text-brand-orange">fino a 50</p>
                <p className="mt-3 text-sm text-white/60">cocktail al minuto — capacità teorica dichiarata*</p>
              </div>
              <div className="rounded-3xl border border-white/[0.08] bg-brand-ink/60 p-8 text-center">
                <p className="font-display text-5xl md:text-6xl font-semibold text-brand-orange">3.000</p>
                <p className="mt-3 text-sm text-white/60">drink all'ora — capacità teorica dichiarata*</p>
              </div>
            </div>
            <p className="mt-6 text-[11px] leading-relaxed text-white/40 max-w-3xl">
              *Dato di riferimento dichiarato a titolo teorico, da verificare con il fornitore e
              mediante test operativi: non costituisce una prestazione garantita o certificata. La
              produttività reale dipende dal numero di erogatori in rete, dai tempi di pagamento, dai
              rifornimenti e dal flusso del pubblico: valutiamo insieme la configurazione giusta per
              la tua manifestazione.
            </p>
          </Reveal>
        </div>
      </section>

      {/* --- Trasparenza --- */}
      <section data-testid="pub-trasparenza" className="pb-24 md:pb-32">
        <div className="mx-auto max-w-7xl px-5 md:px-8">
          <Reveal>
            <p className="eyebrow mb-4">Come funziona la partnership commerciale</p>
            <h2 className="font-display text-3xl md:text-5xl font-semibold leading-tight max-w-4xl">
              OGNI VENDITA CONTA. OGNI INCASSO DEVE ESSERE CHIARO.
            </h2>
          </Reveal>
          <div className="mt-12 border-t border-white/[0.07] max-w-4xl">
            {TRASPARENZA.map((s, i) => (
              <Reveal key={s.t} delay={i * 0.05}>
                <div className="flex items-start gap-6 border-b border-white/[0.07] py-7">
                  <span className="font-display text-2xl font-semibold text-white/25 shrink-0">0{i + 1}</span>
                  <div>
                    <p className="font-display text-xl md:text-2xl font-semibold">{s.t}</p>
                    <p className="mt-2 text-sm leading-relaxed text-white/55">{s.d}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal>
            <p className="mt-8 text-[11px] leading-relaxed text-white/40 max-w-3xl">
              I riepiloghi delle vendite e le modalità con cui consultarli vengono definiti
              nell'accordo commerciale.
            </p>
          </Reveal>
        </div>
      </section>

      {/* --- Iscrizione + calcolo guadagno --- */}
      <section id="iscrizione" className="pb-24 md:pb-32 scroll-mt-24">
        <div className="mx-auto max-w-7xl px-5 md:px-8">
          <div className="grid gap-10 lg:grid-cols-2 items-start">
            <Reveal>
              <div data-testid="pub-iscrizione-card" className="rounded-3xl border border-brand-orange/30 bg-brand-orange/[0.06] p-8">
                <p className="eyebrow mb-3">Iscrivi la tua manifestazione</p>
                {!iscritto ? (
                  <form onSubmit={submitIscrizione} noValidate>
                    <p className="text-sm leading-relaxed text-white/65 mb-6">
                      Iscriviti ora: ti ricontattiamo per valutare insieme come collaborare nello
                      specifico e sbloccare subito il calcolo del tuo guadagno.
                    </p>
                    <div className="grid gap-5 sm:grid-cols-2">
                      {campo("nome", "Nome e cognome *", "nome_cognome", "text", "Mario Rossi")}
                      {campo("organizzazione", "Organizzazione *", "azienda", "text", "Pro Loco, agenzia, associazione…")}
                    </div>
                    <div className="grid gap-5 sm:grid-cols-2 mt-5">
                      {campo("email", "Email *", "email", "email", "tu@organizzazione.it")}
                      {campo("telefono", "Telefono *", "telefono", "tel", "+39 …")}
                    </div>
                    <div className="mt-5">
                      <label htmlFor="pub-spazio" className="block text-[11px] uppercase tracking-[0.18em] text-white/50 mb-2">
                        Spazio disponibile *
                      </label>
                      <select
                        id="pub-spazio"
                        data-testid="pub-field-spazio"
                        className={`field ${err.spazio_disponibile ? "border-red-500/70" : ""}`}
                        value={form.spazio_disponibile}
                        onChange={set("spazio_disponibile")}
                      >
                        <option value="">Seleziona…</option>
                        {SPAZI.map((s) => (
                          <option key={s} value={s}>
                            {s}
                          </option>
                        ))}
                      </select>
                      {err.spazio_disponibile && <p className="mt-2 text-xs text-red-400">{err.spazio_disponibile}</p>}
                    </div>
                    <button
                      type="submit"
                      disabled={sending}
                      data-testid="pub-iscriviti-btn"
                      className="mt-8 w-full inline-flex items-center justify-center gap-3 rounded-full bg-brand-orange px-8 py-4 text-xs font-semibold uppercase tracking-[0.2em] text-brand-ink hover:bg-[#ff9433] transition-colors duration-300 disabled:opacity-50"
                    >
                      {sending ? "Invio…" : "Iscrivi la tua manifestazione"}
                    </button>
                    <p className="mt-4 text-[11px] text-white/40 text-center">
                      Iscrizione gratuita e senza impegno.
                    </p>
                  </form>
                ) : (
                  <div data-testid="pub-iscritto-ok" className="text-center py-10">
                    <CheckCircle2 size={42} className="mx-auto text-brand-orange" />
                    <p className="mt-5 font-display text-2xl font-semibold">Iscrizione ricevuta!</p>
                    <p className="mt-3 text-sm leading-relaxed text-white/60 max-w-sm mx-auto">
                      Ti ricontattiamo al più presto per definire la collaborazione nello specifico.
                      Qui sotto hai già il calcolo del guadagno potenziale della tua manifestazione.
                    </p>
                    <Link
                      to="/partner"
                      data-testid="eventi-pubblici-partner-cta"
                      className="mt-8 inline-flex items-center gap-2 rounded-full border border-brand-orange/50 px-7 py-3.5 text-xs font-semibold uppercase tracking-[0.2em] text-brand-orange hover:bg-brand-orange hover:text-brand-ink transition-colors duration-300"
                    >
                      Vai al modulo completo <ArrowUpRight size={15} />
                    </Link>
                  </div>
                )}
              </div>
            </Reveal>

            <Reveal delay={0.1}>
              <div
                id="calcolo-utile"
                data-testid="calcolo-utile-card"
                className="rounded-3xl border border-white/[0.08] bg-brand-surface/30 p-8 relative overflow-hidden scroll-mt-28"
              >
                {!iscritto && (
                  <div data-testid="calcolo-lock" className="absolute inset-0 z-10 grid place-items-center bg-brand-ink/75 backdrop-blur-md p-8 text-center">
                    <div>
                      <Lock size={30} className="mx-auto text-brand-orange" />
                      <p className="mt-4 font-display text-2xl font-semibold">Calcolo riservato agli iscritti</p>
                      <p className="mt-3 text-sm leading-relaxed text-white/60 max-w-xs mx-auto">
                        Iscrivi la tua manifestazione e scopri subito quanto può guadagnare la tua
                        organizzazione.
                      </p>
                    </div>
                  </div>
                )}
                <div className={iscritto ? "" : "blur-sm select-none pointer-events-none"} aria-hidden={!iscritto}>
                  <p className="eyebrow mb-6">Il tuo guadagno da partner socio</p>
                  <label htmlFor="drinks-stima" className="block text-[11px] uppercase tracking-[0.18em] text-white/50 mb-2">
                    Potenziale drink serviti
                  </label>
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      data-testid="eventi-pubblici-drink-minus"
                      onClick={() => setDrinksSafe(drinks - 100)}
                      className="h-11 w-11 shrink-0 rounded-full border border-white/20 text-white/70 hover:border-brand-orange hover:text-brand-orange transition-colors duration-200"
                      aria-label="Riduci la stima di 100 drink"
                    >
                      <Minus size={15} className="mx-auto" />
                    </button>
                    <input
                      id="drinks-stima"
                      type="number"
                      min="0"
                      max="50000"
                      data-testid="eventi-pubblici-drink-input"
                      value={drinks}
                      onChange={(e) => setDrinksSafe(Number(e.target.value))}
                      className="field text-center font-display text-2xl font-semibold"
                    />
                    <button
                      type="button"
                      data-testid="eventi-pubblici-drink-plus"
                      onClick={() => setDrinksSafe(drinks + 100)}
                      className="h-11 w-11 shrink-0 rounded-full border border-white/20 text-white/70 hover:border-brand-orange hover:text-brand-orange transition-colors duration-200"
                      aria-label="Aumenta la stima di 100 drink"
                    >
                      <Plus size={15} className="mx-auto" />
                    </button>
                  </div>
                  <div className="mt-5 flex flex-wrap gap-2">
                    {[300, 500, 1000, 2000].map((n) => (
                      <button
                        key={n}
                        type="button"
                        data-testid={`eventi-pubblici-preset-${n}`}
                        onClick={() => setDrinks(n)}
                        className={`rounded-full border px-4 py-2 text-xs transition-colors duration-200 ${
                          drinks === n
                            ? "border-brand-orange text-brand-orange"
                            : "border-white/15 text-white/60 hover:border-brand-orange/50 hover:text-brand-orange"
                        }`}
                      >
                        {n} drink
                      </button>
                    ))}
                  </div>
                  <div className="mt-8 rounded-3xl border border-white/[0.08] bg-brand-ink/60 p-8 text-center">
                    <p className="eyebrow">Il tuo utile</p>
                    <p
                      data-testid="eventi-pubblici-utile"
                      className="mt-4 font-display text-5xl md:text-6xl font-semibold text-brand-orange"
                    >
                      {fmt(utile)}
                    </p>
                    <p className="mt-4 text-sm leading-relaxed text-white/60">
                      {drinks} drink × 1,50 € — riconosciuti all'organizzazione, partner socio
                      Sprizziamoci.
                    </p>
                  </div>
                  <p className="mt-5 text-[11px] leading-relaxed text-white/45">
                    La remunerazione effettiva, le modalità di rendicontazione, i tempi di pagamento
                    e le condizioni di partecipazione vengono formalizzati nell'accordo commerciale.
                  </p>
                  <a
                    href={CONTACTS.whatsapp}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="eventi-pubblici-whatsapp"
                    className="mt-6 inline-flex items-center gap-2 rounded-full border border-white/25 px-8 py-4 text-xs font-semibold uppercase tracking-[0.2em] text-white hover:border-brand-orange hover:text-brand-orange transition-colors duration-300"
                  >
                    Scrivici su WhatsApp
                  </a>
                </div>
                <p className="mt-6 text-[11px] leading-relaxed text-white/40">
                  Ogni collaborazione viene valutata a discrezione dell'organizzazione Sprizziamoci,
                  in base allo spazio, al pubblico e al tipo di manifestazione.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </article>
  );
}
